import LoginBox from "./LoginBox";
import { useLanguage } from "../i18n/LanguageContext";

const ceCourses = [
  {
    id: 1,
    badgeKey: "home.mostPopular",
    titleKey: "home.deepTissueCourse",
    ceHours: 6,
    price: "$129",
    descriptionKey: "home.deepTissueCourseDesc",
  },
  {
    id: 2,
    badgeKey: null,
    titleKey: "home.anatomyCourse",
    ceHours: 4,
    price: "$99",
    descriptionKey: "home.anatomyCourseDesc",
  },
  {
    id: 3,
    badgeKey: null,
    titleKey: "home.painManagementCourse",
    ceHours: 6,
    price: "$129",
    descriptionKey: "home.painManagementCourseDesc",
  },
];


function formatDate(dateValue) {
  if (!dateValue) {
    return null;
  }

  const date =
    new Date(dateValue);

  if (
    Number.isNaN(
      date.getTime()
    )
  ) {
    return null;
  }

  return date.toLocaleDateString(
    "en-US",
    {
      month: "short",
      day: "numeric",
      year: "numeric",
    }
  );
}


export default function HomePage({
  session,
  entitlements,
  loadingEntitlements,

  email,
  setEmail,

  password,
  setPassword,

  isAdmin,

  handleLogin,
  handleLogout,

  styles,

  isPasswordRecovery,

  newPassword,
  setNewPassword,

  handleUpdatePassword,

  forgotEmail,
  setForgotEmail,

  handleForgotPassword,

  authMode,
  setAuthMode,

  signupUsername,
  setSignupUsername,

  signupFullName,
  setSignupFullName,

  handleSignup,

  setPage,

  openLearningCenter,
  openVocabulary,
  openPractice,
  openWrongQuestionCenter,
}) {
  const { t } = useLanguage();
  // =========================================================
  // SUBSCRIPTION / ACCESS
  // =========================================================

  const isPremium =
    entitlements?.has_premium_access === true;

  const isTrialing =
    entitlements?.is_trialing === true;

  const isSuperuser =
    entitlements?.is_superuser === true;

  const subscriptionStatus =
    entitlements?.subscription_status ||
    "free";

  const paymentType =
    entitlements?.payment_type;

  const accessSource =
    entitlements?.access_source || null;

  const isManualAccess =
    accessSource === "manual";

  const questionsUsed =
    entitlements?.questions_used ??
    0;

  const questionsRemaining =
    entitlements?.questions_remaining;

  const currentPeriodEnd =
    formatDate(
      entitlements?.current_period_end
    );

  const trialEnd =
    formatDate(
      entitlements?.trial_end
    );

  const cancelAtPeriodEnd =
    entitlements?.cancel_at_period_end ===
    true;


  // =========================================================
  // MEMBERSHIP LABELS
  // =========================================================

  const getPlanLabel = () => {
    if (isSuperuser) {
      return t("home.superUser");
    }

    if (isTrialing) {
      return t("home.premiumTrial");
    }

    if (isPremium) {
      return t("home.premium");
    }

    return t("home.free");
  };


  const getMembershipStatus = () => {
    if (isSuperuser) {
      return t("home.permanentAccess");
    }

    if (subscriptionStatus === "past_due") {
      return t("home.paymentIssue");
    }

    if (isTrialing) {
      return t("home.trialActive");
    }

    // Manual plans are active until their expiration date.
    // They are not Stripe subscriptions scheduled for cancellation.
    if (
      isManualAccess &&
      subscriptionStatus === "active"
    ) {
      return t("home.active");
    }

    if (subscriptionStatus === "active") {
      return cancelAtPeriodEnd
        ? t("home.cancellationScheduled")
        : t("home.active");
    }

    if (
      subscriptionStatus === "canceled" ||
      subscriptionStatus === "expired"
    ) {
      return t("home.ended");
    }

    return t("home.free");
  };

  const getMembershipSubtext = () => {
    if (isSuperuser) {
      return t("home.fullPremiumAccess");
    }

    if (isTrialing && trialEnd) {
      return t("home.trialEnds").replace(
        "{date}",
        trialEnd
      );
    }

    if (
      isManualAccess &&
      subscriptionStatus === "active" &&
      currentPeriodEnd
    ) {
      return t("home.accessUntil").replace(
        "{date}",
        currentPeriodEnd
      );
    }

    if (
      subscriptionStatus === "active" &&
      cancelAtPeriodEnd &&
      currentPeriodEnd
    ) {
      return t("home.accessUntil").replace(
        "{date}",
        currentPeriodEnd
      );
    }

    if (
      subscriptionStatus === "active" &&
      currentPeriodEnd
    ) {
      return t("home.renewsOn").replace(
        "{date}",
        currentPeriodEnd
      );
    }

    if (subscriptionStatus === "past_due") {
      return t("home.updatePaymentMethod");
    }

    return t("home.upgradeAnytime");
  };


  // =========================================================
  // DISPLAY NAME
  // =========================================================

  const displayName =
    session?.user?.user_metadata
      ?.full_name ||
    session?.user?.user_metadata
      ?.username ||
    session?.user?.email
      ?.split("@")?.[0] ||
    "Student";


  // =========================================================
  // MAIN PRODUCT CARDS
  //
  // All question-related functionality now routes through
  // Supabase-based Practice / Wrong Question architecture.
  // There is no local question-bank navigation here.
  // =========================================================

  const productCards = [
    {
      title: t("home.practiceQuestions"),
      description: t("home.practiceQuestionsDesc"),
      icon: "🎯",
      action: openPractice,
      locked: false,
    },

    {
      title: t("home.learningCenter"),
      description: t("home.learningCenterDesc"),
      icon: "📚",
      action: openLearningCenter,
      locked: !entitlements?.can_use_learning_center,
    },

    {
      title: t("home.vocabulary"),
      description: t("home.vocabularyDesc"),
      icon: "🧠",
      action: openVocabulary,
      locked: !entitlements?.can_use_vocabulary,
    },

    {
      title: t("home.wrongQuestions"),
      description: t("home.wrongQuestionsDesc"),
      icon: "📊",
      action: openWrongQuestionCenter,
      locked: false,
    },
  ];


  const handleProductClick =
    (item) => {

      if (!item.locked) {
        item.action?.();

        return;
      }

      setPage(
        "account"
      );
    };


  // =========================================================
  // RENDER
  // =========================================================

  return (
    <div
      style={
        pageStyles.page
      }
    >
      <div
        style={
          pageStyles.container
        }
      >

        {/* ===================================================
            HEADER
            Only show the original header after login
        =================================================== */}

        {session && !isPasswordRecovery && (
          <header
            style={
              pageStyles.header
            }
          >
            <div>
              <div
                style={
                  pageStyles.brandRow
                }
              >
                <div
                  style={
                    pageStyles.logoCircle
                  }
                >
                  ✦
                </div>

                <div>
                  <h1
                    style={
                      pageStyles.appTitle
                    }
                  >
                    {t("home.appTitle")}
                  </h1>

                  <div
                    style={
                      pageStyles.tagline
                    }
                  >
                    {t("home.appTagline")}
                  </div>
                </div>
              </div>
            </div>

            <button
              style={
                pageStyles.accountIconButton
              }
              onClick={() =>
                setPage("account")
              }
              title="My Account"
            >
              👤
            </button>
          </header>
        )}

        {/* ===================================================
            LOGIN / USER SUMMARY
        =================================================== */}

        {!session ? (
          <section
            style={
              pageStyles.loginSection
            }
          >
            <LoginBox
              session={
                session
              }

              email={
                email
              }

              setEmail={
                setEmail
              }

              password={
                password
              }

              setPassword={
                setPassword
              }

              isAdmin={
                isAdmin
              }

              handleLogin={
                handleLogin
              }

              handleLogout={
                handleLogout
              }

              styles={
                styles
              }

              isPasswordRecovery={
                isPasswordRecovery
              }

              newPassword={
                newPassword
              }

              setNewPassword={
                setNewPassword
              }

              handleUpdatePassword={
                handleUpdatePassword
              }

              forgotEmail={
                forgotEmail
              }

              setForgotEmail={
                setForgotEmail
              }

              handleForgotPassword={
                handleForgotPassword
              }

              authMode={
                authMode
              }

              setAuthMode={
                setAuthMode
              }

              signupUsername={
                signupUsername
              }

              setSignupUsername={
                setSignupUsername
              }

              signupFullName={
                signupFullName
              }

              setSignupFullName={
                setSignupFullName
              }

              handleSignup={
                handleSignup
              }

              setPage={
                setPage
              }
            />
          </section>
        ) : (
          !isPasswordRecovery && (
            <section
              style={
                pageStyles.memberPanel
              }
            >

              {/* =============================================
                  MEMBER WELCOME
              ============================================= */}

              <div
                style={
                  pageStyles.memberWelcome
                }
              >
                <div
                  style={
                    pageStyles.welcomeSmall
                  }
                >
                  {t("home.welcomeBack")}
                </div>

                <div
                  style={
                    pageStyles.memberName
                  }
                >
                  {displayName}
                </div>

                <div
                  style={
                    pageStyles.welcomeText
                  }
                >
                  {t("home.keepBuilding")}
                  <br />
                  {t("home.stayConsistent")}
                </div>

                <div style={pageStyles.accountActions}>
                <button
                  style={pageStyles.primaryButton}
                  onClick={() =>
                    setPage("account")
                  }
                >
                  👤&nbsp;&nbsp;{t("home.myAccount")}
                </button>

                <div style={pageStyles.secondaryActions}>
                  {(isAdmin || isSuperuser) && (
                    <button
                      style={pageStyles.adminButton}
                      onClick={() =>
                        setPage("admin")
                      }
                    >
                      ♛&nbsp;&nbsp;Admin Dashboard
                    </button>
                  )}

                  <button
                    style={pageStyles.logoutButton}
                    onClick={handleLogout}
                  >
                    ↪&nbsp;&nbsp;{t("home.logout")}
                  </button>
                </div>
              </div>
              </div>


              {/* =============================================
                  ACCOUNT STATS
              ============================================= */}

              <div
                style={
                  pageStyles.statsPanel
                }
              >

                <div
                  style={
                    pageStyles.statItem
                  }
                >
                  <div
                    style={
                      pageStyles.statLabel
                    }
                  >
                    {t("home.currentAccess")}
                  </div>

                  <div
                    style={{
                      ...pageStyles.planBadge,

                      ...(isPremium ||
                      isSuperuser
                        ? pageStyles.premiumBadge
                        : pageStyles.freeBadge),
                    }}
                  >
                    {getPlanLabel()}
                  </div>

                  {paymentType && (
                    <div
                      style={
                        pageStyles.statSub
                      }
                    >
                      {paymentType === "monthly"
                        ? t("home.monthlyPlan")
                        : paymentType === "quarterly"
                        ? t("home.quarterlyPlan")
                        : paymentType}
                    </div>
                  )}
                </div>


                <div
                  style={
                    pageStyles.statItem
                  }
                >
                  <div
                    style={
                      pageStyles.statLabel
                    }
                  >
                    {t("home.questionsUsed")}
                  </div>

                  <div
                    style={
                      pageStyles.statValue
                    }
                  >
                    {questionsUsed}
                  </div>

                  <div
                    style={
                      pageStyles.statSub
                    }
                  >
                    {isPremium || isSuperuser
                      ? t("home.premiumAccess")
                      : t("home.ofFreeQuestions")}
                  </div>
                </div>


                <div
                  style={
                    pageStyles.statItem
                  }
                >
                  <div
                    style={
                      pageStyles.statLabel
                    }
                  >
                    {t("home.questionsRemaining")}
                  </div>

                  <div
                    style={
                      pageStyles.statValue
                    }
                  >
                    {isPremium || isSuperuser
                      ? t("home.unlimited")
                      : questionsRemaining ?? 0}
                  </div>

                  <div
                    style={
                      pageStyles.statSub
                    }
                  >
                    {isPremium || isSuperuser
                      ? t("home.fullQuestionBank")
                      : t("home.freeAccess")}
                  </div>
                </div>


                <div
                  style={
                    pageStyles.statItem
                  }
                >
                  <div
                    style={
                      pageStyles.statLabel
                    }
                  >
                    {t("home.membershipStatus")}
                  </div>

                  <div
                    style={
                      pageStyles.statusValue
                    }
                  >
                    {loadingEntitlements
                      ? t("home.loading")
                      : getMembershipStatus()}
                  </div>

                  <div
                    style={
                      pageStyles.statSub
                    }
                  >
                    {getMembershipSubtext()}
                  </div>
                </div>

              </div>
            </section>
          )
        )}


        {/* ===================================================
            STUDY + PRACTICE
        =================================================== */}

        {session &&
          !isPasswordRecovery && (
            <>

              <section
                style={
                  pageStyles.section
                }
              >
                <div
                  style={
                    pageStyles.sectionIntro
                  }
                >
                  <div
                    style={
                      pageStyles.sectionIcon
                    }
                  >
                    🎯
                  </div>

                  <div>
                    <h2
                      style={
                        pageStyles.sectionTitle
                      }
                    >
                      {t("home.studyPractice")}
                    </h2>

                    <p
                      style={
                        pageStyles.sectionDescription
                      }
                    >
                      {t("home.studyPracticeDesc")}
                    </p>
                  </div>
                </div>


                <div
                  style={
                    pageStyles.productGrid
                  }
                >
                  {productCards.map(
                    (item) => (
                      <button
                        key={
                          item.title
                        }
                        style={
                          pageStyles.productCard
                        }
                        onClick={() =>
                          handleProductClick(
                            item
                          )
                        }
                      >
                        <div
                          style={
                            pageStyles.productIcon
                          }
                        >
                          {item.icon}
                        </div>

                        <div
                          style={
                            pageStyles.productTitleRow
                          }
                        >
                          <div
                            style={
                              pageStyles.productTitle
                            }
                          >
                            {item.title}
                          </div>

                          {item.locked && (
                            <div
                              style={
                                pageStyles.lockBadge
                              }
                            >
                              Premium
                            </div>
                          )}
                        </div>

                        <div
                          style={
                            pageStyles.productDescription
                          }
                        >
                          {item.description}
                        </div>

                        <div
                          style={
                            pageStyles.cardArrow
                          }
                        >
                          {item.locked
                            ? "🔒"
                            : "→"}
                        </div>
                      </button>
                    )
                  )}
                </div>
              </section>


              {/* =============================================
                  LEARNING PROMO
              ============================================= */}

              <section
                style={{
                  ...pageStyles.section,
                  ...pageStyles.learningSection,
                }}
              >
                <div
                  style={
                    pageStyles.promoContent
                  }
                >
                  <div
                    style={
                      pageStyles.sectionIcon
                    }
                  >
                    📖
                  </div>

                  <h2
                    style={
                      pageStyles.sectionTitle
                    }
                  >
                    {t("home.continueEducation")}
                  </h2>

                  <p
                    style={
                      pageStyles.sectionDescription
                    }
                  >
                    {t("home.continueEducationDesc")}
                  </p>

                  <div
                    style={
                      pageStyles.featureList
                    }
                  >
                    <div>
                      ✓ {t("home.structuredStudyMaterials")}
                    </div>

                    <div>
                      ✓ {t("home.examStrategyReview")}
                    </div>

                    <div>
                      ✓ {t("home.anatomyRefreshers")}
                    </div>

                    <div>
                      ✓ {t("home.ethicsProfessionalConcepts")}
                    </div>
                  </div>

                  <button
                    style={
                      pageStyles.secondaryButton
                    }
                    onClick={
                      openLearningCenter
                    }
                  >
                    {t("home.goToLearningCenter")}
                  </button>
                </div>


                <div
                  style={
                    pageStyles.promoVisual
                  }
                >
                  <div
                    style={
                      pageStyles.visualEmoji
                    }
                  >
                    🌿
                  </div>

                  <div
                    style={
                      pageStyles.visualTitle
                    }
                  >
                    {t("home.buildKnowledge")}
                    <br />
                    {t("home.buildConfidence")}
                  </div>

                  <div
                    style={
                      pageStyles.visualText
                    }
                  >
                    {t("home.learnAnyDevice")}
                  </div>
                </div>
              </section>


              {/* ===================================================
                  CE COURSES — COMING SOON
              =================================================== */}

              <section
                style={{
                  ...pageStyles.section,
                  ...pageStyles.ceSection,
                }}
              >
                {/* -------------------------------------------------
                    EXISTING CE CONTENT
                    Kept in the background for future launch.
                ------------------------------------------------- */}

                <div style={pageStyles.ceBackgroundContent}>
                  <div style={pageStyles.ceHeader}>
                    <div>
                      <div style={pageStyles.sectionIcon}>
                        🎓
                      </div>

                      <h2 style={pageStyles.sectionTitle}>
                        {t("home.advanceCareer")}
                      </h2>

                      <p style={pageStyles.sectionDescription}>
                        {t("home.ceProfessionalDesc")}
                      </p>
                    </div>

                    <button
                      style={pageStyles.secondaryButton}
                      disabled
                    >
                      {t("home.browseCeCourses")}
                    </button>
                  </div>

                  <div style={pageStyles.courseGrid}>
                    {ceCourses.map((course) => (
                      <div
                        key={course.id}
                        style={pageStyles.courseCard}
                      >
                        <div style={pageStyles.courseImage}>
                          <span
                            style={{
                              fontSize: 42,
                            }}
                          >
                            💆
                          </span>

                          {course.badgeKey && (
                            <div style={pageStyles.courseBadge}>
                              {t(course.badgeKey)}
                            </div>
                          )}
                        </div>

                        <div style={pageStyles.courseBody}>
                          <h3 style={pageStyles.courseTitle}>
                            {t(course.titleKey)}
                          </h3>

                          <div style={pageStyles.courseMeta}>
                            <span style={pageStyles.ceHoursBadge}>
                              {t("home.ceHours").replace(
                                "{count}",
                                course.ceHours
                              )}
                            </span>

                            <span style={pageStyles.priceBadge}>
                              {course.price}
                            </span>
                          </div>

                          <p style={pageStyles.courseDescription}>
                            {t(course.descriptionKey)}
                          </p>

                          <button
                            style={pageStyles.courseButton}
                            disabled
                          >
                            {t("home.viewCourse")}
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>


                {/* -------------------------------------------------
                    COMING SOON FROSTED OVERLAY
                ------------------------------------------------- */}

                <div style={pageStyles.ceComingSoonOverlay}>
                  <div style={pageStyles.ceComingSoonContent}>

                    <div style={pageStyles.ceComingSoonIcon}>
                      🎓
                    </div>

                    <div style={pageStyles.ceComingSoonLabel}>
                      {t("home.comingSoon")}
                    </div>

                    <h2 style={pageStyles.ceComingSoonTitle}>
                      {t("home.ceCoursesComingSoon")}
                    </h2>

                    <p style={pageStyles.ceComingSoonDescription}>
                      {t("home.ceComingSoonDesc")}
                    </p>

                    <div style={pageStyles.ceComingSoonFeatures}>

                      <div style={pageStyles.ceComingSoonFeature}>
                        <div style={pageStyles.ceFeatureIcon}>
                          💻
                        </div>

                        <div>
                          <div style={pageStyles.ceFeatureTitle}>
                            {t("home.onlineLearning")}
                          </div>

                          <div style={pageStyles.ceFeatureText}>
                            {t("home.onlineLearningDesc")}
                          </div>
                        </div>
                      </div>


                      <div style={pageStyles.ceComingSoonFeature}>
                        <div style={pageStyles.ceFeatureIcon}>
                          📜
                        </div>

                        <div>
                          <div style={pageStyles.ceFeatureTitle}>
                            {t("home.continuingEducation")}
                          </div>

                          <div style={pageStyles.ceFeatureText}>
                            {t("home.continuingEducationDesc")}
                          </div>
                        </div>
                      </div>


                      <div style={pageStyles.ceComingSoonFeature}>
                        <div style={pageStyles.ceFeatureIcon}>
                          👩‍🏫
                        </div>

                        <div>
                          <div style={pageStyles.ceFeatureTitle}>
                            {t("home.experiencedInstructors")}
                          </div>

                          <div style={pageStyles.ceFeatureText}>
                            {t("home.experiencedInstructorsDesc")}
                          </div>
                        </div>
                      </div>

                    </div>

                    <p style={pageStyles.ceComingSoonFooterText}>
                      {t("home.ceComingSoonFooter")}
                    </p>

                  </div>
                </div>
              </section>


              {/* =============================================
                  VALUE PROPS
              ============================================= */}

              <section
                style={
                  pageStyles.valueStrip
                }
              >
                <div
                  style={
                    pageStyles.valueItem
                  }
                >
                  <span
                    style={
                      pageStyles.valueIcon
                    }
                  >
                    🎯
                  </span>

                  <div>
                    <strong>
                      {t("home.examFocused")}
                    </strong>

                    <div style={pageStyles.valueText}>
                      {t("home.examFocusedDesc")}
                    </div>
                  </div>
                </div>


                <div
                  style={
                    pageStyles.valueItem
                  }
                >
                  <span
                    style={
                      pageStyles.valueIcon
                    }
                  >
                    📘
                  </span>

                  <div>
                    <strong>
                      {t("home.structuredLearning")}
                    </strong>

                    <div style={pageStyles.valueText}>
                      {t("home.structuredLearningDesc")}
                    </div>
                  </div>
                </div>


                <div
                  style={
                    pageStyles.valueItem
                  }
                >
                  <span
                    style={
                      pageStyles.valueIcon
                    }
                  >
                    📈
                  </span>

                  <div>
                    <strong>
                      {t("home.trackProgress")}
                    </strong>

                    <div style={pageStyles.valueText}>
                      {t("home.trackProgressDesc")}
                    </div>
                  </div>
                </div>


                <div
                  style={
                    pageStyles.valueItem
                  }
                >
                  <span
                    style={
                      pageStyles.valueIcon
                    }
                  >
                    💻
                  </span>

                  <div>
                    <strong>
                      {t("home.anywhereAnytime")}
                    </strong>

                    <div style={pageStyles.valueText}>
                      {t("home.anywhereAnytimeDesc")}
                    </div>
                  </div>
                </div>
              </section>

            </>
          )}

        {/* ===================================================
            FOOTER
        =================================================== */}

        <footer
          style={
            pageStyles.footer
          }
        >
          <div>
            {t("footer.copyright")}
          </div>

          <div style={pageStyles.footerLinks}>
            <button
              type="button"
              onClick={() =>
                setPage("terms")
              }
              style={pageStyles.footerLinkButton}
            >
              {t("footer.terms")}
            </button>

            <button
              type="button"
              onClick={() =>
                setPage("privacy")
              }
              style={pageStyles.footerLinkButton}
            >
              {t("footer.privacy")}
            </button>

            <button
              type="button"
              onClick={() =>
                setPage("contact")
              }
              style={pageStyles.footerLinkButton}
            >
              {t("footer.contact")}
            </button>
          </div>
        </footer>

      </div>
    </div>
  );
}


// ============================================================
// PAGE STYLES
// ============================================================

const pageStyles = {

  page: {
    minHeight:
      "100vh",

    background:
      "linear-gradient(180deg, #f7f9fc 0%, #ffffff 100%)",

    padding:
      "28px 18px 50px",

    fontFamily:
      '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',

    color:
      "#172033",
  },


  container: {
    width:
      "100%",

    maxWidth:
      "1180px",

    margin:
      "0 auto",
  },


  header: {
    display:
      "flex",

    alignItems:
      "center",

    justifyContent:
      "space-between",

    marginBottom:
      "26px",

    padding:
      "0 6px",
  },


  brandRow: {
    display:
      "flex",

    alignItems:
      "center",

    gap:
      "14px",
  },


  logoCircle: {
    width:
      "48px",

    height:
      "48px",

    borderRadius:
      "16px",

    background:
      "#e9f0ff",

    color:
      "#2766df",

    display:
      "flex",

    alignItems:
      "center",

    justifyContent:
      "center",

    fontSize:
      "24px",

    fontWeight:
      800,
  },


  appTitle: {
    margin:
      0,

    fontSize:
      "30px",

    lineHeight:
      1.2,

    fontWeight:
      800,

    letterSpacing:
      "-0.7px",
  },


  tagline: {
    marginTop:
      "5px",

    color:
      "#64748b",

    fontSize:
      "14px",
  },


  accountIconButton: {
    width:
      "46px",

    height:
      "46px",

    borderRadius:
      "50%",

    border:
      "1px solid #e2e8f0",

    background:
      "#ffffff",

    cursor:
      "pointer",

    fontSize:
      "18px",

    boxShadow:
      "0 4px 16px rgba(15,23,42,0.05)",
  },


  loginSection: {
    background:
      "#fff",

    borderRadius:
      "18px",

    padding:
      "22px",

    boxShadow:
      "0 10px 35px rgba(15,23,42,0.07)",

    marginBottom:
      "26px",
  },


  memberPanel: {
    background:
      "#ffffff",

    borderRadius:
      "20px",

    padding:
      "26px",

    display:
      "grid",

    gridTemplateColumns:
      "280px 1fr",

    gap:
      "30px",

    marginBottom:
      "26px",

    boxShadow:
      "0 10px 35px rgba(15,23,42,0.07)",

    border:
      "1px solid #edf1f7",
  },


  memberWelcome: {
    paddingRight:
      "10px",
  },


  welcomeSmall: {
    color:
      "#64748b",

    fontSize:
      "16px",

    marginBottom:
      "4px",
  },


  memberName: {
    fontSize:
      "27px",

    fontWeight:
      800,

    marginBottom:
      "10px",
  },


  welcomeText: {
    color:
      "#64748b",

    lineHeight:
      1.55,

    marginBottom:
      "18px",
  },

  accountActions: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    gap: "10px",
  },


  secondaryActions: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    flexWrap: "wrap",
  },


  primaryButton: {
    background:
      "linear-gradient(135deg, #2563eb 0%, #2f6df6 100%)",

    color: "#ffffff",

    border: "none",

    borderRadius: "12px",

    padding: "12px 20px",

    minWidth: "200px",

    fontWeight: 700,

    fontSize: "15px",

    cursor: "pointer",

    boxShadow:
      "0 5px 14px rgba(37, 99, 235, 0.18)",
  },


  adminButton: {
    background:
      "linear-gradient(135deg, #faf7ff 0%, #f5f0ff 100%)",

    color: "#5b35d5",

    border:
      "1px solid #c9b8ff",

    borderRadius: "12px",

    padding: "11px 16px",

    fontWeight: 700,

    fontSize: "14px",

    cursor: "pointer",

    whiteSpace: "nowrap",
  },


  logoutButton: {
    background: "#ffffff",

    color: "#475569",

    border:
      "1px solid #cbd5e1",

    borderRadius: "12px",

    padding: "11px 16px",

    fontWeight: 600,

    fontSize: "14px",

    cursor: "pointer",

    whiteSpace: "nowrap",
  },

  statsPanel: {
    display:
      "grid",

    gridTemplateColumns:
      "repeat(4, 1fr)",

    border:
      "1px solid #e2e8f0",

    borderRadius:
      "16px",

    overflow:
      "hidden",
  },


  statItem: {
    padding:
      "22px 16px",

    textAlign:
      "center",

    borderRight:
      "1px solid #e2e8f0",
  },


  statLabel: {
    color:
      "#64748b",

    fontSize:
      "12px",

    marginBottom:
      "14px",
  },


  statValue: {
    fontSize:
      "23px",

    fontWeight:
      800,

    marginBottom:
      "5px",
  },


  statusValue: {
    color:
      "#16a34a",

    fontSize:
      "20px",

    fontWeight:
      800,

    lineHeight:
      1.2,

    marginBottom:
      "5px",

    whiteSpace:
      "normal",

    overflowWrap:
      "anywhere",

    wordBreak:
      "normal",

    maxWidth:
      "100%",
  },

  statSub: {
    color:
      "#64748b",

    fontSize:
      "12px",
  },


  planBadge: {
    display:
      "inline-block",

    borderRadius:
      "999px",

    padding:
      "6px 12px",

    fontWeight:
      700,

    fontSize:
      "13px",

    marginBottom:
      "5px",
  },


  premiumBadge: {
    background:
      "#ecfdf3",

    color:
      "#15803d",
  },


  freeBadge: {
    background:
      "#f1f5f9",

    color:
      "#475569",
  },


  section: {
    background:
      "#ffffff",

    border:
      "1px solid #e2e8f0",

    borderRadius:
      "20px",

    padding:
      "28px",

    marginBottom:
      "24px",

    boxShadow:
      "0 8px 26px rgba(15,23,42,0.045)",
  },


  sectionIntro: {
    marginBottom:
      "24px",
  },


  sectionIcon: {
    width:
      "48px",

    height:
      "48px",

    borderRadius:
      "16px",

    background:
      "#eef4ff",

    display:
      "flex",

    alignItems:
      "center",

    justifyContent:
      "center",

    fontSize:
      "23px",

    marginBottom:
      "14px",
  },


  sectionTitle: {
    margin:
      "0 0 8px",

    fontSize:
      "24px",

    fontWeight:
      800,
  },


  sectionDescription: {
    color:
      "#64748b",

    lineHeight:
      1.6,

    margin:
      0,
  },


  productGrid: {
    display:
      "grid",

    gridTemplateColumns:
      "repeat(auto-fit, minmax(210px, 1fr))",

    gap:
      "16px",
  },


  productCard: {
    position:
      "relative",

    minHeight:
      "220px",

    border:
      "1px solid #e2e8f0",

    borderRadius:
      "16px",

    padding:
      "22px",

    textAlign:
      "left",

    background:
      "#ffffff",

    cursor:
      "pointer",

    transition:
      "all 0.2s ease",
  },


  productIcon: {
    width:
      "46px",

    height:
      "46px",

    background:
      "#f1f5ff",

    borderRadius:
      "14px",

    display:
      "flex",

    alignItems:
      "center",

    justifyContent:
      "center",

    fontSize:
      "23px",

    marginBottom:
      "20px",
  },


  productTitleRow: {
    display:
      "flex",

    alignItems:
      "center",

    justifyContent:
      "space-between",

    gap:
      "8px",
  },


  productTitle: {
    fontSize:
      "17px",

    fontWeight:
      800,
  },


  productDescription: {
    color:
      "#64748b",

    fontSize:
      "14px",

    lineHeight:
      1.6,

    marginTop:
      "10px",

    paddingRight:
      "6px",
  },


  lockBadge: {
    background:
      "#fff7ed",

    color:
      "#c2410c",

    borderRadius:
      "999px",

    padding:
      "4px 7px",

    fontSize:
      "10px",

    fontWeight:
      700,
  },


  cardArrow: {
    position:
      "absolute",

    right:
      "20px",

    bottom:
      "18px",

    fontSize:
      "19px",

    color:
      "#2563eb",
  },


  learningSection: {
    display:
      "grid",

    gridTemplateColumns:
      "1fr 1fr",

    gap:
      "28px",

    background:
      "linear-gradient(135deg, #fffdf8 0%, #ffffff 100%)",
  },


  promoContent: {
    padding:
      "8px",
  },


  featureList: {
    color:
      "#475569",

    lineHeight:
      1.9,

    margin:
      "20px 0",

    fontSize:
      "14px",
  },


  promoVisual: {
    minHeight:
      "290px",

    borderRadius:
      "18px",

    padding:
      "34px",

    display:
      "flex",

    flexDirection:
      "column",

    justifyContent:
      "center",

    background:
      "linear-gradient(135deg, #effbf5 0%, #eef5ff 100%)",
  },


  visualEmoji: {
    fontSize:
      "54px",

    marginBottom:
      "20px",
  },


  visualTitle: {
    fontSize:
      "27px",

    fontWeight:
      800,

    lineHeight:
      1.2,

    marginBottom:
      "14px",
  },


  visualText: {
    color:
      "#64748b",
  },


  secondaryButton: {
    border:
      "1px solid #2563eb",

    background:
      "#ffffff",

    color:
      "#2563eb",

    borderRadius:
      "10px",

    padding:
      "11px 16px",

    fontWeight:
      700,

    cursor:
      "pointer",
  },


  ceSection: {
    position: "relative",

    overflow: "hidden",

    minHeight: "610px",

    background:
      "linear-gradient(135deg, #fff 0%, #fbf8ff 100%)",
  },


  ceHeader: {
    display:
      "flex",

    alignItems:
      "end",

    justifyContent:
      "space-between",

    gap:
      "20px",

    marginBottom:
      "24px",
  },


  courseGrid: {
    display:
      "grid",

    gridTemplateColumns:
      "repeat(auto-fit, minmax(240px, 1fr))",

    gap:
      "18px",
  },


  courseCard: {
    border:
      "1px solid #e2e8f0",

    borderRadius:
      "16px",

    overflow:
      "hidden",

    background:
      "#fff",
  },


  courseImage: {
    height:
      "135px",

    position:
      "relative",

    display:
      "flex",

    alignItems:
      "center",

    justifyContent:
      "center",

    background:
      "linear-gradient(135deg, #eaf2ff 0%, #f6f1ff 100%)",
  },


  courseBadge: {
    position:
      "absolute",

    top:
      "10px",

    left:
      "10px",

    background:
      "#2563eb",

    color:
      "#fff",

    borderRadius:
      "999px",

    padding:
      "5px 9px",

    fontSize:
      "10px",

    fontWeight:
      700,
  },


  courseBody: {
    padding:
      "18px",
  },


  courseTitle: {
    margin:
      "0 0 12px",

    fontSize:
      "17px",

    minHeight:
      "42px",
  },


  courseMeta: {
    display:
      "flex",

    gap:
      "8px",

    marginBottom:
      "12px",
  },


  ceHoursBadge: {
    background:
      "#eff6ff",

    color:
      "#2563eb",

    borderRadius:
      "999px",

    padding:
      "5px 9px",

    fontSize:
      "11px",

    fontWeight:
      700,
  },


  priceBadge: {
    background:
      "#f1f5f9",

    color:
      "#475569",

    borderRadius:
      "999px",

    padding:
      "5px 9px",

    fontSize:
      "11px",

    fontWeight:
      700,
  },


  courseDescription: {
    color:
      "#64748b",

    fontSize:
      "13px",

    lineHeight:
      1.6,

    minHeight:
      "62px",
  },


  courseButton: {
    marginTop:
      "6px",

    width:
      "100%",

    border:
      "none",

    borderRadius:
      "9px",

    background:
      "#f1f5f9",

    padding:
      "9px 12px",

    fontWeight:
      700,

    cursor:
      "pointer",
  },


  valueStrip: {
    display:
      "grid",

    gridTemplateColumns:
      "repeat(auto-fit, minmax(200px, 1fr))",

    gap:
      "18px",

    padding:
      "22px",

    marginBottom:
      "24px",

    borderRadius:
      "18px",

    background:
      "linear-gradient(135deg, #effbf5 0%, #f5fbff 100%)",

    border:
      "1px solid #dcefe7",
  },


  valueItem: {
    display:
      "flex",

    gap:
      "12px",

    alignItems:
      "flex-start",
  },


  valueIcon: {
    fontSize:
      "22px",
  },


  valueText: {
    color:
      "#64748b",

    fontSize:
      "12px",

    lineHeight:
      1.5,

    marginTop:
      "4px",
  },


  footer: {
    textAlign:
      "center",

    color:
      "#64748b",

    fontSize:
      "13px",

    padding:
      "24px 0 10px",
  },


  footerLinks: {
    display:
      "flex",

    justifyContent:
      "center",

    gap:
      "20px",

    marginTop:
      "10px",
  },

  ceBackgroundContent: {
    filter: "blur(1.5px)",

    opacity: 0.65,

    pointerEvents: "none",

    userSelect: "none",
  },

  ceComingSoonOverlay: {
    position: "absolute",

    inset: 0,

    zIndex: 10,

    display: "flex",

    alignItems: "center",

    justifyContent: "center",

    padding: "40px",

    boxSizing: "border-box",

    background:
      "rgba(248, 250, 252, 0.48)",

    backdropFilter:
      "blur(5px)",

    WebkitBackdropFilter:
      "blur(5px)",
  },

  ceComingSoonContent: {
    width: "100%",

    maxWidth: "820px",

    textAlign: "center",
  },

  ceComingSoonIcon: {
    width: "64px",

    height: "64px",

    margin: "0 auto 16px",

    borderRadius: "20px",

    display: "flex",

    alignItems: "center",

    justifyContent: "center",

    background: "#eef4ff",

    fontSize: "30px",
  },


  ceComingSoonLabel: {
    display: "inline-block",

    marginBottom: "12px",

    padding: "6px 12px",

    borderRadius: "999px",

    background: "#e8f0ff",

    color: "#2563eb",

    fontSize: "12px",

    fontWeight: 800,

    letterSpacing: "1.5px",
  },


  ceComingSoonTitle: {
    margin: "0 0 14px",

    color: "#172033",

    fontSize: "32px",

    fontWeight: 850,

    letterSpacing: "-0.5px",
  },


  ceComingSoonDescription: {
    maxWidth: "680px",

    margin: "0 auto",

    color: "#64748b",

    fontSize: "17px",

    lineHeight: 1.7,
  },


  ceComingSoonFeatures: {
    display: "grid",

    gridTemplateColumns:
      "repeat(auto-fit, minmax(200px, 1fr))",

    gap: "18px",

    marginTop: "32px",
  },


  ceComingSoonFeature: {
    padding: "20px",

    borderRadius: "16px",

    border:
      "1px solid rgba(226, 232, 240, 0.9)",

    background:
      "rgba(255, 255, 255, 0.72)",

    textAlign: "left",
  },


  ceFeatureIcon: {
    fontSize: "26px",

    marginBottom: "10px",
  },


  ceFeatureTitle: {
    color: "#172033",

    fontSize: "15px",

    fontWeight: 800,

    marginBottom: "5px",
  },


  ceFeatureText: {
    color: "#64748b",

    fontSize: "13px",

    lineHeight: 1.55,
  },


  ceComingSoonFooterText: {
    maxWidth: "650px",

    margin: "28px auto 0",

    color: "#64748b",

    fontSize: "14px",

    lineHeight: 1.65,
  },

  footerLinkButton: {
    border: "none",

    padding: 0,

    background: "transparent",

    color: "#64748b",

    fontSize: "13px",

    fontFamily: "inherit",

    cursor: "pointer",
  },
};